/**
 * The card tint TOKEN SET — the value half of `tints.ts`, with no CSS attached.
 *
 * ## Why this is a separate file from `tints.ts`
 *
 * It is not a taste call. `tints.ts` imports `./WidgetCard.module.css`, which makes
 * it unloadable from the ROOT jest runner: `jest.config.js` declares
 * `transform: { '^.+\\.ts$': ['@swc/jest'] }` and no `moduleNameMapper` for CSS, so
 * a `.css` file is handed to the VM untransformed and dies on `SyntaxError:
 * Unexpected token '.'`. (The web-app's vitest is fine — vite proxies CSS modules
 * natively.)
 *
 * `widgetDefinitionAdapter` — which has to validate `tint` at the domain/builder
 * seam (t34) — is imported by BOTH runners: the web-app vitest suites AND
 * `src/tests/src/analytics/widgetGrammar.adapter.test.ts`, the 26-test ajv oracle
 * that is the only place the REAL `AddUiWidgetLayoutCommandSchema` and the adapter
 * meet. So the adapter cannot import `tints.ts`, and re-spelling the seven names
 * inside the adapter is the exact drift `tints.ts` was written to prevent: a token
 * added to the palette but missing from the save-path guard is dropped on save and
 * looks, to the author, like the colour simply did not stick.
 *
 * The split is therefore along the only line that matters — **tokens have no CSS,
 * the class table does.** `tints.ts` re-exports everything here, so it remains the
 * one import site for every renderer and for `WidgetInspector`; nothing outside
 * this file and `tints.ts` should need to import it directly (the adapter is the
 * deliberate exception, for the reason above).
 *
 * ⚠️ Everything about WHY these are tokens rather than free hex — the per-theme
 * `light-dark()` resolution, the contrast floor, the rejected `{ light, dark }`
 * widening, and where `tint` lives on the data (the layout WRAPPER, never the
 * definition) — is documented in `tints.ts`. Read it before touching this list.
 */

/**
 * The seven tokens, in swatch order. `none` leads because it is the default and
 * the way back.
 *
 * This list is the FRONT-END mirror of the domain enum
 * (`UiWidgetsLayoutSchema.widgets[].tint`, `uiWidgets-generic.types.ts`). The two
 * must agree: a token here that the domain does not declare is an ajv rejection on
 * save, and a token the domain declares but this omits is silently dropped by the
 * adapter's guard.
 */
export const WIDGET_TINT_TOKENS = [ 'none', 'teal', 'amber', 'green', 'red', 'navy', 'slate' ] as const;

export type WidgetTint = typeof WIDGET_TINT_TOKENS[number];

const TINT_TOKEN_SET: ReadonlySet<string> = new Set<string>( WIDGET_TINT_TOKENS );

/**
 * `true` for one of the seven names.
 *
 * Guards the READ side. `UiWidgetsLayoutSchema` constrains `tint` to this enum,
 * so a value from the server is already one of them — but a layout can also
 * arrive from an older build, a hand-edited fixture or the AI preview path, and
 * an unknown token must degrade to "no tint" rather than to a class name that
 * resolves to nothing.
 *
 * TV1-2291 (t34) — it now also guards the WRITE side, in
 * `widgetDefinitionAdapter.setTintIfValid`. Same predicate, deliberately: the
 * renderer's "degrade to no tint" and the adapter's "do not put this on the wire"
 * are the same question asked at two seams, and answering it twice is how they
 * come to disagree.
 */
export const isWidgetTint = ( value: unknown ): value is WidgetTint =>
  typeof value === 'string' && TINT_TOKEN_SET.has( value );

/**
 * A six- or eight-digit hex, and NOTHING else.
 *
 * ⚠️ THIS IS A SECURITY BOUNDARY, not a formatting nicety (2026-08-17). A custom
 * tint is written into a CSS custom property on a SHARED document, so a value that
 * is not constrained here is one author injecting CSS into another user's page —
 * `url(…)`, a `;` closing the declaration, `var(--…)` reading a value it should not.
 * The anchored pattern admits `#rrggbb` / `#rrggbbaa` and rejects everything else,
 * including the three- and four-digit shorthands (no reason to accept two spellings
 * of one value) and any leading or trailing whitespace.
 *
 * ⚠️ ALPHA IS PART OF THE COLOUR (2026-08-18). The trailing byte is opacity, so
 * `#66ff0080` is that green at half strength. Widening from six digits to "six or
 * eight" is backward-compatible in both directions that matter: every stored
 * `#rrggbb` still passes, and a card whose tint carries alpha degrades to an
 * untinted card on a front end that predates this, rather than to a broken one.
 *
 * It MIRRORS the domain pattern on `UiWidgetsLayoutSchema.widgets[].tint`, and is
 * applied again at render rather than trusted from the schema: a persisted layout
 * can predate any schema version, and the renderer is the last line before the
 * value reaches the DOM.
 */
const CUSTOM_TINT_PATTERN = /^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/;

/** A custom colour, as opposed to one of the seven names. */
export type CustomTint = string;

export const isCustomTint = ( value: unknown ): value is CustomTint =>
  typeof value === 'string' && CUSTOM_TINT_PATTERN.test( value );

/** Either spelling of a tint: one of the seven names, or a validated hex. */
export const isTintValue = ( value: unknown ): boolean =>
  isWidgetTint( value ) || isCustomTint( value );
